import React, { useCallback, useState } from 'react';
import { supabase } from '@/utils/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Loader2, Mail, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ReviewRequestBackfillPanel', 'review-backfill');

interface PreviewRecipientCounts {
  borrower: number;
  co_borrower: number;
  realtor: number;
  borrower_email_missing?: number;
  realtor_email_missing_purchase?: number;
}

interface PreviewResponse {
  dryRun: boolean;
  candidateLoans: number;
  /** Capped sample (≤20) of loan numbers in the dry-run cohort, most-recently-funded first.
   *  Lets admins spot-check the cohort BEFORE running for real. */
  candidate_loans_sample?: string[];
  recipients: PreviewRecipientCounts;
  realtor_email_coverage_pct: number;
  warnings: string[];
  missing_realtor_loans_sample: string[];
}

interface ExecuteResponse {
  dryRun: false;
  candidateLoans: number;
  totalRecipients: number;
  sent: number;
  skipped: number;
  failed: number;
  warnings: string[];
}

/** Snapshotted filter params at preview time. Live send uses these, NOT current state,
 *  so a filter edit between preview and execute can't sneak in a different cohort. */
interface PreviewSnapshot {
  fundedSinceMonths: number;
  loanNumberPrefix: string;
  includeCoBorrowers: boolean;
  includeRealtors: boolean;
}

function paramsEqual(a: PreviewSnapshot | null, b: PreviewSnapshot): boolean {
  if (!a) {
return false;
}
  return (
    a.fundedSinceMonths === b.fundedSinceMonths &&
    a.loanNumberPrefix === b.loanNumberPrefix &&
    a.includeCoBorrowers === b.includeCoBorrowers &&
    a.includeRealtors === b.includeRealtors
  );
}

export const ReviewRequestBackfillPanel: React.FC = () => {
  const [fundedSinceMonths, setFundedSinceMonths] = useState<number>(4);
  const [loanNumberPrefix, setLoanNumberPrefix] = useState<string>('LW');
  const [includeCoBorrowers, setIncludeCoBorrowers] = useState<boolean>(true);
  const [includeRealtors, setIncludeRealtors] = useState<boolean>(true);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewSnapshot, setPreviewSnapshot] = useState<PreviewSnapshot | null>(null);
  const [executeResult, setExecuteResult] = useState<ExecuteResponse | null>(null);

  // If any filter changes after a preview, clear the stale preview so the
  // "Run for real" button disables until the user previews the new filters.
  const currentParams: PreviewSnapshot = {
    fundedSinceMonths,
    loanNumberPrefix,
    includeCoBorrowers,
    includeRealtors
  };
  const previewMatchesFilters = paramsEqual(previewSnapshot, currentParams);

  const handlePreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(null);
    setPreviewSnapshot(null);
    setExecuteResult(null);
    const snapshot: PreviewSnapshot = {
      fundedSinceMonths,
      loanNumberPrefix,
      includeCoBorrowers,
      includeRealtors
    };
    try {
      const { data, error } = await supabase.functions.invoke<PreviewResponse>(
        'send-review-request-backfill',
        {
          method: 'POST',
          body: { dryRun: true, ...snapshot }
        }
      );
      if (error) {
        log.error('preview failed', { error }, error as Error);
        toast.error(`Preview failed: ${error.message}`);
        return;
      }
      if (!data) {
        toast.error('Preview returned no data');
        return;
      }
      setPreview(data);
      // Lock the snapshot ONLY after a successful preview — live send uses
      // these values, not the current input state.
      setPreviewSnapshot(snapshot);
      toast.success(`Preview ready — ${data.candidateLoans} candidate loans`);
    } catch (e) {
      log.error('preview threw', { error: e }, e as Error);
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  }, [fundedSinceMonths, loanNumberPrefix, includeCoBorrowers, includeRealtors]);

  const handleExecute = useCallback(async () => {
    if (!preview || !previewSnapshot) {
      return;
    }
    const totalRecipients =
      preview.recipients.borrower + preview.recipients.co_borrower + preview.recipients.realtor;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Send ${totalRecipients} review-request emails to ${preview.recipients.borrower} borrowers, ` +
        `${preview.recipients.co_borrower} co-borrowers, and ${preview.recipients.realtor} realtors? This cannot be undone.`
    );
    if (!ok) {
      return;
    }

    setExecuting(true);
    try {
      const { data, error } = await supabase.functions.invoke<ExecuteResponse>(
        'send-review-request-backfill',
        {
          method: 'POST',
          body: {
            dryRun: false,
            // Use the SNAPSHOT, not current state — defends against any
            // filter edit between preview and execute (we also gate the button
            // on previewMatchesFilters, but defense in depth).
            ...previewSnapshot
          }
        }
      );
      if (error) {
        log.error('execute failed', { error }, error as Error);
        toast.error(`Backfill failed: ${error.message}`);
        return;
      }
      if (!data) {
        toast.error('Backfill returned no data');
        return;
      }
      setExecuteResult(data);
      toast.success(
        `Backfill done — sent ${data.sent}, skipped ${data.skipped}, failed ${data.failed}`
      );
    } catch (e) {
      log.error('execute threw', { error: e }, e as Error);
      toast.error('Backfill failed');
    } finally {
      setExecuting(false);
    }
  }, [preview, previewSnapshot]);

  return (
    <div className="space-y-4 rounded-md border border-amber-200 bg-amber-50/40 p-4">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-amber-700" />
        <span className="text-sm font-medium text-amber-900">Historical backfill</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Send review-request emails to past funded loans matching the filter below. Dry-run first;
        the live send button only enables after a successful preview.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="backfill-prefix" className="text-xs">
            Loan number prefix
          </Label>
          <Input
            id="backfill-prefix"
            value={loanNumberPrefix}
            onChange={(e) => setLoanNumberPrefix(e.target.value)}
            className="h-8 text-xs"
            placeholder="LW"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="backfill-months" className="text-xs">
            Funded in last N months
          </Label>
          <Input
            id="backfill-months"
            type="number"
            min={1}
            max={36}
            value={fundedSinceMonths}
            onChange={(e) => setFundedSinceMonths(parseInt(e.target.value, 10) || 4)}
            className="h-8 text-xs"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={includeCoBorrowers}
            onCheckedChange={(checked) => setIncludeCoBorrowers(checked === true)}
          />
          Include co-borrowers
        </label>
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={includeRealtors}
            onCheckedChange={(checked) => setIncludeRealtors(checked === true)}
          />
          Include realtors (purchase only)
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
          onClick={handlePreview}
          disabled={previewing || executing}
        >
          {previewing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Previewing…
            </>
          ) : (
            <>
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              Preview (dry run)
            </>
          )}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          className="h-8 text-xs"
          onClick={handleExecute}
          disabled={!preview || !previewMatchesFilters || executing || previewing}
        >
          {executing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Sending…
            </>
          ) : (
            <>Run for real</>
          )}
        </Button>
      </div>

      {preview && !previewMatchesFilters && (
        <div className="flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          <AlertTriangle className="h-3.5 w-3.5" />
          Filters changed since preview — re-run preview before sending.
        </div>
      )}

      {preview && (
        <div className="space-y-2 rounded-md bg-background p-3 text-xs">
          <div className="font-medium">Preview results</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div>Candidate loans:</div>
            <div className="font-medium">{preview.candidateLoans}</div>
            <div>Borrower recipients:</div>
            <div className="font-medium">{preview.recipients.borrower}</div>
            {preview.recipients.borrower_email_missing !== undefined && (
              <>
                <div className="text-muted-foreground">Borrower email missing:</div>
                <div>{preview.recipients.borrower_email_missing}</div>
              </>
            )}
            {includeCoBorrowers && (
              <>
                <div>Co-borrower recipients:</div>
                <div className="font-medium">{preview.recipients.co_borrower}</div>
              </>
            )}
            {includeRealtors && (
              <>
                <div>Realtor recipients:</div>
                <div className="font-medium">{preview.recipients.realtor}</div>
                <div className="text-muted-foreground">Realtor email coverage:</div>
                <div>{preview.realtor_email_coverage_pct}%</div>
              </>
            )}
          </div>
          {preview.warnings.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
              <div className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-3.5 w-3.5" />
                Warnings
              </div>
              <ul className="mt-1 list-disc pl-4 space-y-0.5">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {preview.candidate_loans_sample && preview.candidate_loans_sample.length > 0 && (
            <details className="text-muted-foreground">
              <summary className="cursor-pointer">
                Sample of {preview.candidate_loans_sample.length} candidate loans (most recently
                funded first)
              </summary>
              <div className="mt-1 font-mono text-[11px]">
                {preview.candidate_loans_sample.join(', ')}
              </div>
            </details>
          )}
          {preview.missing_realtor_loans_sample.length > 0 && (
            <details className="text-muted-foreground">
              <summary className="cursor-pointer">
                Sample of {preview.missing_realtor_loans_sample.length} purchase loans missing
                realtor email
              </summary>
              <div className="mt-1 font-mono text-[11px]">
                {preview.missing_realtor_loans_sample.join(', ')}
              </div>
            </details>
          )}
        </div>
      )}

      {executeResult && (
        <div className="space-y-1 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          <div className="font-medium">Backfill complete</div>
          <div>
            Sent: <span className="font-medium">{executeResult.sent}</span> / Skipped:{' '}
            <span className="font-medium">{executeResult.skipped}</span> / Failed:{' '}
            <span className="font-medium">{executeResult.failed}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewRequestBackfillPanel;
